import { atom, atomFamily, selector, selectorFamily, waitForAll } from 'recoil';
import {
  DEFAULT_LANDSCAPE_HEIGHT,
  DEFAULT_MARGIN,
  DEFAULT_PORTRAIT_HEIGHT,
  PAGE_HEADER_HEIGHT,
} from './printConstants';
import type { AnalysisView, Portfolio, PrintOrientationTypeEnum } from 'venn-api';
import { resetOnStudioReset } from '../../effects/signalEffects';
import { blockSubjectInputGroups, subjectInputGroupSubjects } from './input-management/subjectInput';
import {
  hasFeesAndExcludedInvestments,
  hasFeatureSelector,
  isAnalysisBlock,
  isHoldingsBlock,
  PageType,
  getSubjectNodesWithExcludedFees,
  getRequestSubjectDisplayZeroFee,
} from 'venn-utils';
import type { Page } from 'venn-components';
import { flatten, uniq, zip } from 'lodash';
import { isReportState } from './view';
import { displayZeroFeeState } from './advisoryFeeConfig';
import type { BlockId, StudioRequestSubject } from '../types';
import { blockCustomMetricSettingsState, blockSettings } from './blockSettings';
import { requestSubjects } from './subjects';
import { allSubjects } from './allViewSubjects';
import { blockSelectedCustomizableMetrics } from './blockConfig';

export interface ReportingPageGroup {
  startIndex: number;
  views: AnalysisView[];
  totalHeight: number;
  /** If current width is not full width */
  placeholderHeight?: number;
  /** Views that maybe be merged with others in one row */
  placeholderViews: AnalysisView[];
}

export const studioPrintOrientationType = atom<PrintOrientationTypeEnum>({
  key: 'studioPrintOrientationType',
  default: 'PORTRAIT',
  effects: [resetOnStudioReset],
});

export const studioIsPortrait = selector<boolean>({
  key: 'studioIsPortrait',
  get: ({ get }) => get(studioPrintOrientationType) === 'PORTRAIT',
});

export const studioFooterHeight = atom<number>({
  key: 'studioFooterHeight',
  default: DEFAULT_MARGIN,
  effects: [resetOnStudioReset],
});

export const footerMeasurePageIndexState = selector<number>({
  key: 'footerMeasurePageIndex',
  get: ({ get }) => get(viewPages).findIndex((p) => p.type === PageType.GRID || p.type === PageType.CONTENTS),
});

export const studioHideHeaderViewName = atom<boolean>({
  key: 'studioHideHeaderViewName',
  default: false,
  effects: [resetOnStudioReset],
});
export const studioHideHeaderCompanyName = atom<boolean>({
  key: 'studioHideHeaderCompanyName',
  default: false,
  effects: [resetOnStudioReset],
});
export const studioHideHeaderLogo = atom<boolean>({
  key: 'studioHideHeaderLogo',
  default: false,
  effects: [resetOnStudioReset],
});

export const studioHideFooterPagination = atom<boolean>({
  key: 'studioHideFooterPagination',
  default: false,
  effects: [resetOnStudioReset],
});
export const studioHideFooterDisclaimer = atom<boolean>({
  key: 'studioHideFooterDisclaimer',
  default: false,
  effects: [resetOnStudioReset],
});

export const studioCustomFooterText = atom<string | undefined>({
  key: 'studioCustomFooterText',
  default: undefined,
  effects: [resetOnStudioReset],
});

export const studioTitlePageHasFullPageBackgroundImage = atom<boolean>({
  key: 'studioTitlePageHasFullPageBackgroundImage',
  default: false,
  effects: [resetOnStudioReset],
});

export const studioTitlePageHasDisclosureTextAlignedRight = atom<boolean>({
  key: 'studioTitlePageHasDisclosureTextAlignedRight',
  default: false,
  effects: [resetOnStudioReset],
});
export const studioTitlePageHideCompanyLogo = atom<boolean>({
  key: 'studioTitlePageHideCompanyLogo',
  default: false,
  effects: [resetOnStudioReset],
});
export const studioSectionPageHasFullPageBackgroundImage = atom<boolean>({
  key: 'studioSectionPageHasFullPageBackgroundImage',
  default: false,
  effects: [resetOnStudioReset],
});

export const studioSectionPageHideCompanyName = atom<boolean>({
  key: 'studioSectionPageHideCompanyName',
  default: false,
  effects: [resetOnStudioReset],
});

export const studioHidePageHeader = selector<boolean>({
  key: 'studioHidePageHeader',
  get: ({ get }) => get(studioHideHeaderViewName) && get(studioHideHeaderCompanyName) && get(studioHideHeaderLogo),
});

export const studioPageHeight = selector<number>({
  key: 'studioPageHeight',
  get: ({ get }) => (get(studioIsPortrait) ? DEFAULT_PORTRAIT_HEIGHT : DEFAULT_LANDSCAPE_HEIGHT),
});

export const studioContentHeight = selector<number>({
  key: 'studioContentHeight',
  get: ({ get }) => {
    const reservedSpace = (get(studioHidePageHeader) ? DEFAULT_MARGIN : PAGE_HEADER_HEIGHT) + get(studioFooterHeight);
    return get(studioPageHeight) - reservedSpace;
  },
});

export const viewPages = atom<Page[]>({
  key: 'viewPages',
  default: [
    {
      layout: [],
      type: PageType.GRID,
    },
  ],
  effects: [resetOnStudioReset],
});

export const isReturnsBasedBlockState = selectorFamily<boolean, BlockId>({
  key: 'isReturnsBasedBlock',
  get:
    (id) =>
    ({ get }) => {
      const blockType = get(blockSettings(id)).customBlockType;
      return isAnalysisBlock(blockType) && blockType !== 'PORTFOLIO_BREAKDOWN';
    },
});

export const isHoldingsBasedBlockState = selectorFamily<boolean, BlockId>({
  key: 'isHoldingsBasedBlock',
  get:
    (id) =>
    ({ get }) => {
      const blockType = get(blockSettings(id)).customBlockType;
      return isHoldingsBlock(blockType);
    },
});

export const blocksOnPage = selectorFamily<BlockId[], number>({
  key: 'blocksOnPage',
  get:
    (pageNumber) =>
    ({ get }) => {
      const allPages = get(viewPages);
      const page = allPages[pageNumber]!;
      if (page.type !== PageType.GRID) {
        return [];
      }
      return page.layout.map((layout) => layout.i);
    },
});

export const hasCustomMetricsOnPage = selectorFamily<boolean, number>({
  key: 'hasCustomMetricsOnPage',
  get:
    (pageNumber) =>
    ({ get }) => {
      const blocks = get(blocksOnPage(pageNumber));
      const blocksCustomMetrics = get(waitForAll(blocks.map((id) => blockCustomMetricSettingsState(id))));
      return blocksCustomMetrics.some((customMetrics) => customMetrics.length !== 0);
    },
});

export const hasCustomMetadataOnPage = selectorFamily<boolean, number>({
  key: 'hasCustomMetadataOnPage',
  get:
    (pageNumber) =>
    ({ get }) => {
      const blocks = get(blocksOnPage(pageNumber));
      const blocksMetrics = flatten(get(waitForAll(blocks.map((id) => blockSelectedCustomizableMetrics(id)))));
      return blocksMetrics.some((metric) => metric.analysisType === 'CUSTOM_METADATA');
    },
});

export const hasExcludedFeesOnPage = selectorFamily<boolean, number>({
  key: 'hasExcludedFeesOnPage',
  get:
    (pageNumber) =>
    ({ get }) => {
      if (!get(isReportState)) {
        return false;
      }
      const blocks = get(blocksOnPage(pageNumber)).filter((id) => get(isReturnsBasedBlockState(id)));
      const subjectGroups = uniq(get(waitForAll(blocks.map((id) => blockSubjectInputGroups(id)))).flat());
      const subjects = get(waitForAll(subjectGroups.map((id) => subjectInputGroupSubjects(id)))).flat();

      const subjectRequests: StudioRequestSubject[] = get(requestSubjects(subjects));
      return subjectRequests.some((subject) => hasFeesAndExcludedInvestments({ subject }));
    },
});

export const hasVennDataModificationsOnPage = selectorFamily<boolean, number>({
  key: 'hasVennDataModificationsOnPage',
  get:
    (pageNumber) =>
    ({ get }) => {
      if (!get(isReportState)) {
        return false;
      }
      const blocks = get(blocksOnPage(pageNumber));
      const blocksHavingDataModifications = get(waitForAll(blocks.map((blockId) => hasVennDataModifications(blockId))));
      return blocksHavingDataModifications.some(Boolean);
    },
});

export const allSubjectsWithExcludedInvestmentFees = selector<
  { subject: StudioRequestSubject; excluded: Portfolio[] }[]
>({
  key: 'allSubjectsWithExcludedInvestmentFees',
  get: ({ get }) => {
    const allSubjectsData = get(allSubjects);
    const subjectsWithExcludedFees = get(requestSubjects(allSubjectsData)).filter((subject) =>
      hasFeesAndExcludedInvestments({
        subject,
        displayZeroFee: getRequestSubjectDisplayZeroFee({
          subject,
          displayZeroFeeConfigValue: get(displayZeroFeeState),
          isReport: get(isReportState),
          displayDefaultFee: get(hasFeatureSelector('default_rl_advisory_fees_ff')),
          displayInvestmentFee: get(hasFeatureSelector('advisory_fees_for_rl_investments_ff')),
        }),
      }),
    );

    const excludedSubjects = getSubjectNodesWithExcludedFees(subjectsWithExcludedFees);
    return zip(subjectsWithExcludedFees, excludedSubjects).map(([subject, excluded]) => ({
      subject: subject!,
      excluded: excluded!,
    }));
  },
});

export const hasVennDataModifications = atomFamily<boolean, BlockId>({
  key: 'hasVennDataModifications',
  default: false,
  effects: [resetOnStudioReset],
});
